import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Event as EventIcon,
  LocalHospital as LocalHospitalIcon,
  Notes as NotesIcon,
  Description as DescriptionIcon,
  MedicalServices as MedicalServicesIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { apiClient } from '../../services/api.js';

const MedicalRecordDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [medicalRecord, setMedicalRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [billingRecords, setBillingRecords] = useState([]);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  
  useEffect(() => {
    const fetchMedicalRecordData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Import the MedicalRecordsService
        const MedicalRecordsService = (await import('../../services/MedicalRecordsService')).default;
        
        // Fetch medical record from the API
        const response = await MedicalRecordsService.getMedicalRecordById(id);
        const record = response.data;
        const recordId = record?.recordId ?? record?.RecordId;
        const patient = record?.patient ?? record?.Patient;
        const doctor = record?.doctor ?? record?.Doctor;
        
        // Transform the data to match the expected format
        const medicalRecord = {
          id: recordId,
          patientId: record?.patientId ?? record?.PatientId,
          patientName: patient ? `${patient.firstName ?? patient.FirstName ?? ''} ${patient.lastName ?? patient.LastName ?? ''}`.trim() || 'Unknown' : 'Unknown',
          date: record?.visitDate ?? record?.VisitDate,
          type: record?.chiefComplaint ?? record?.ChiefComplaint, // Using chief complaint as type
          physician: doctor ? `Dr. ${doctor.lastName ?? doctor.LastName ?? ''}`.trim() || 'Unknown' : 'Unknown', // Changed from doctor to physician
          diagnosis: record?.diagnosis ?? record?.Diagnosis,
          symptoms: record?.chiefComplaint ?? record?.ChiefComplaint, // Using chief complaint as symptoms
          treatment: record?.treatment ?? record?.Treatment,
          notes: record?.notes ?? record?.Notes,
          attachments: [], // Will be loaded separately
          createdAt: record?.createdAt ?? record?.CreatedAt,
          updatedAt: record?.updatedAt ?? record?.UpdatedAt,
          pulseDiagnosis: record?.pulseDiagnosis ?? record?.PulseDiagnosis ?? '',
          tongueExamination: record?.tongueExamination ?? record?.TongueExamination ?? '',
          constitutionAssessment: record?.constitutionAssessment ?? record?.ConstitutionAssessment ?? '',
          syndromePattern: record?.syndromePattern ?? record?.SyndromePattern ?? '',
          herbalPrescription: record?.herbalPrescription ?? record?.HerbalPrescription ?? '',
          acupuncturePoints: record?.acupuncturePoints ?? record?.AcupuncturePoints ?? '',
          treatmentPrinciple: record?.treatmentPrinciple ?? record?.TreatmentPrinciple ?? '',
          lifestyleAdvice: record?.lifestyleAdvice ?? record?.LifestyleAdvice ?? '',
          followUpPlan: record?.followUpPlan ?? record?.FollowUpPlan ?? ''
        };
        
        setMedicalRecord(medicalRecord);
        
        // Load attachments
        if (recordId != null)
          loadAttachments(recordId);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching medical record data:', error);
        setError('Failed to load medical record data. Please try again later.');
        setLoading(false);
      }
    };
    
    const loadAttachments = async (recordId) => {
      try {
        const response = await apiClient.get(`/attachment/medical-record/${recordId}`);
        
        const attachments = response.data.map(att => ({
          id: att.id,
          name: att.fileName,
          type: att.contentType.split('/')[0],
          size: `${(att.fileSize / (1024 * 1024)).toFixed(1)} MB`,
          uploadedAt: att.uploadedAt,
          uploadedBy: att.uploadedBy
        }));
        
        setMedicalRecord(prev => ({
          ...prev,
          attachments: attachments
        }));
        
      } catch (error) {
        console.error('Error loading attachments:', error);
        // Don't show error for attachments, just log it
      }
    };
    
    fetchMedicalRecordData();
  }, [id]);

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const response = await apiClient.get(`/attachment/download/${attachmentId}`, { responseType: 'blob' });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setError('Failed to download attachment. Please try again.');
    }
  };

  const handleEditMedicalRecord = () => {
    navigate(`/medical-records/${id}/edit`);
  };
  
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleDeleteMedicalRecord = async () => {
    try {
      // Import the MedicalRecordsService
      const MedicalRecordsService = (await import('../../services/MedicalRecordsService')).default;
      
      // Call the API to delete the record
      await MedicalRecordsService.deleteMedicalRecord(id);
      
      handleDeleteDialogClose();
      navigate('/medical-records');
    } catch (error) {
      console.error('Error deleting medical record:', error);
      alert('Failed to delete medical record. Please try again.');
      handleDeleteDialogClose();
    }
  };
  
  // Function to fetch billing records associated with this medical record
  const fetchBillingRecords = async () => {
    try {
      setLoadingBilling(true);
      setBillingError(null);
      
      // Import the MedicalRecordsService
      const MedicalRecordsService = (await import('../../services/MedicalRecordsService')).default;
      
      // Fetch billing records associated with this medical record
      const response = await MedicalRecordsService.getBillingRecordsByMedicalRecordId(id);
      const invoicesData = response?.data;
      const normalizedInvoices = Array.isArray(invoicesData)
        ? invoicesData
        : (Array.isArray(invoicesData?.$values) ? invoicesData.$values : []);

      setBillingRecords(normalizedInvoices);
      setLoadingBilling(false);
    } catch (error) {
      console.error('Error fetching billing records:', error);
      setBillingError('Failed to load billing records. Please try again later.');
      setBillingRecords([]);
      setLoadingBilling(false);
    }
  };
  
  // Function to navigate to billing details
  const viewBillingDetails = (billingId) => {
    navigate(`/billing/invoices/${billingId}`);
  };
  
  // Function to create a new billing record from this medical record
  const createBillingFromMedicalRecord = async () => {
    try {
      // Import the MedicalRecordsService
      const MedicalRecordsService = (await import('../../services/MedicalRecordsService')).default;
      
      // Create a new billing record from this medical record
      const response = await MedicalRecordsService.createBillingFromMedicalRecord(id, {
        patientId: medicalRecord.patientId,
        description: `Billing for ${medicalRecord.type} on ${new Date(medicalRecord.date).toLocaleDateString()}`,
        // Add other billing data as needed
      });
      
      // Navigate to the newly created billing record
      const invoiceId = response?.data?.invoiceId ?? response?.data?.InvoiceId;
      navigate(`/billing/invoices/${invoiceId}`);
    } catch (error) {
      console.error('Error creating billing record:', error);
      alert('Failed to create billing record. Please try again.');
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  if (!medicalRecord) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Medical record not found</Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
          component={RouterLink}
          to="/medical-records"
        >
          Back to Medical Records
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 1 }}
            component={RouterLink}
            to="/medical-records"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Medical Record Details
          </Typography>
        </Box>
        <Box>
          <Tooltip title="View Billing Records">
            <Button
              variant="outlined"
              startIcon={<ReceiptIcon />}
              sx={{ mr: 1 }}
              onClick={() => {
                fetchBillingRecords();
                setBillingDialogOpen(true);
              }}
            >
              Billing
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            sx={{ mr: 1 }}
            onClick={handleEditMedicalRecord}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteDialogOpen}
          >
            Delete
          </Button>
        </Box>
      </Box>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                  {medicalRecord.type}
                </Typography>
                <Chip 
                  label={medicalRecord.diagnosis}
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Patient
                  </Typography>
                  <Typography variant="body1">
                    <Button 
                      component={RouterLink} 
                      to={`/patients/${medicalRecord.patientId}`}
                      sx={{ p: 0, textTransform: 'none' }}
                    >
                      {medicalRecord.patientName}
                    </Button>
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalHospitalIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Physician
                  </Typography>
                  <Typography variant="body1">
                    {medicalRecord.physician}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(medicalRecord.date).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MedicalServicesIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Diagnosis
                  </Typography>
                  <Typography variant="body1">
                    {medicalRecord.diagnosis}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Traditional Chinese Medicine Assessment
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Syndrome Pattern (证候)
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.syndromePattern || 'Not recorded'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Pulse Diagnosis (脉诊)
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.pulseDiagnosis || 'Not recorded'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Tongue Examination (舌诊)
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.tongueExamination || 'Not recorded'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Constitution Assessment (体质辨识)
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.constitutionAssessment || 'Not recorded'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Herbal Prescription (中药处方)
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.herbalPrescription || 'Not recorded'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Acupuncture Points (针灸穴位)
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.acupuncturePoints || 'Not recorded'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Treatment Principle (治法)
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.treatmentPrinciple || 'Not recorded'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Lifestyle Advice (生活调理建议)
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.lifestyleAdvice || 'Not recorded'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Follow-up Plan (复诊计划)
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.followUpPlan || 'Not recorded'}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Symptoms
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.symptoms || 'No symptoms recorded'}
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                Treatment
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.treatment || 'No treatment recorded'}
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                Notes
              </Typography>
              <Typography variant="body1" paragraph>
                {medicalRecord.notes || 'No notes available'}
              </Typography>
            </Grid>
            
            {medicalRecord.attachments && medicalRecord.attachments.length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Attachments
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  {medicalRecord.attachments.map((attachment) => (
                    <Box 
                      key={attachment.id}
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 1,
                        '&:not(:last-child)': { borderBottom: '1px solid #eee' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2">
                          {attachment.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {attachment.size}
                        </Typography>
                        <Tooltip title="Download attachment">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDownloadAttachment(attachment.id, attachment.name)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Created: {new Date(medicalRecord.createdAt).toLocaleString()}
          {medicalRecord.updatedAt !== medicalRecord.createdAt && 
            ` | Last updated: ${new Date(medicalRecord.updatedAt).toLocaleString()}`}
        </Typography>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>Delete Medical Record</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this medical record? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteMedicalRecord} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Billing Records Dialog */}
      <Dialog
        open={billingDialogOpen}
        onClose={() => setBillingDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Billing Records</DialogTitle>
        <DialogContent>
          {loadingBilling ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : billingError ? (
            <Typography color="error">{billingError}</Typography>
          ) : billingRecords.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body1" gutterBottom>
                No billing records found for this medical record.
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<ReceiptIcon />}
                onClick={createBillingFromMedicalRecord}
                sx={{ mt: 2 }}
              >
                Create Billing Record
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" gutterBottom>
                The following billing records are associated with this medical record:
              </Typography>
              <Paper variant="outlined" sx={{ mt: 2 }}>
                {(Array.isArray(billingRecords) ? billingRecords : []).map((billing, index) => {
                  const invoiceId = billing?.invoiceId ?? billing?.InvoiceId;
                  const invoiceNumber = billing?.invoiceNumber ?? billing?.InvoiceNumber ?? '';
                  const invoiceDate = billing?.invoiceDate ?? billing?.InvoiceDate;
                  const status = billing?.status ?? billing?.Status ?? '';
                  const totalAmount = Number(billing?.totalAmount ?? billing?.TotalAmount ?? 0);

                  return (
                  <Box 
                    key={invoiceId ?? invoiceNumber ?? `invoice-${index}`}
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 2,
                      '&:not(:last-child)': { borderBottom: '1px solid #eee' }
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1">
                        Invoice #{invoiceNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(invoiceDate ? new Date(invoiceDate).toLocaleDateString() : '-')} - {status}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        ${Number.isFinite(totalAmount) ? totalAmount.toFixed(2) : '0.00'}
                      </Typography>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => viewBillingDetails(invoiceId)}
                        sx={{ ml: 2 }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </Box>
                  );
                })}
              </Paper>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<ReceiptIcon />}
                onClick={createBillingFromMedicalRecord}
                sx={{ mt: 2 }}
              >
                Create New Billing Record
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalRecordDetails;
